import { z } from 'zod'
import { resultVisibilitySchema, publicFaqEntrySchema } from './competition-settings'
import { studentEligibilityRequirementScopeSchema, enrollmentProofTypeSchema } from './eligibility'
import {
  fieldKeySchema,
  fieldOptionsSchema,
  fieldValidationRulesSchema,
  fieldVisibilitySchema,
  formFieldTypeSchema,
} from './form'
import {
  ianaTimeZoneSchema,
  localeSchema,
  nullableHttpsUrlSchema,
  publicIdSchema,
  slugSchema,
} from './primitives'
import { manualScoringEffectSchema } from './manual-scoring'
import { roundKeySchema, scoringInputModeSchema } from './round'
import { rubricAnchorSchema, scoreTypeSchema } from './rubric'

// ---------------------------------------------------------------------------
// v4.46 Competition Configuration JSON-only. See
// docs/incoming/NAPAICP_Competition_Configuration_JSON_Only_Spec_SDD_v4.46_Proposed.md
// and docs/incoming/NAPAICP_Competition_Configuration_JSON_Only_Autonomous_Execution_Spec_SDD_v4.46.md.
//
// This is the SINGLE canonical shape shared by Export, Import, Clone and
// Save-as-Template (Autonomous Execution Spec B-01: "不得讓 Clone / Export /
// Template 自己各寫 mapping"). It intentionally contains ONLY configuration:
// no participant, team, submission, score, result, invitation or audit data,
// and no database primary keys: cross-references inside a snapshot use
// domain-stable "logical keys" (round `key`, form `type`, rubric `name`,
// category `name`) instead. See `test/competition-configuration.test.ts` for
// the explicit denylist this schema must never grow to satisfy.
// ---------------------------------------------------------------------------

export const CONFIGURATION_FORMAT = 'napaicp.competition-config' as const
export const CONFIGURATION_SCHEMA_VERSION = '1.0.0' as const
/** Major version this importer/adapter chain currently accepts. Bump only via a new Version Adapter (Autonomous Execution Spec B-06). */
export const CONFIGURATION_SUPPORTED_MAJOR = 1 as const

export const configurationDateStrategySchema = z.enum(['clear', 'preserve', 'shift'])
export type ConfigurationDateStrategy = z.infer<typeof configurationDateStrategySchema>

// ---------------------------------------------------------------------------
// Basic
// ---------------------------------------------------------------------------

export const configurationBasicSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  shortDescription: z.string().max(500).nullable(),
  description: z.string().max(20_000).nullable(),
  officialWebsiteUrl: nullableHttpsUrlSchema,
  locale: localeSchema,
  timezone: ianaTimeZoneSchema,
  publicPageEnabled: z.boolean(),
})
export type ConfigurationBasic = z.infer<typeof configurationBasicSchema>

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export const configurationCategorySchema = z.strictObject({
  /** Logical key: category `name`. Must be unique within the snapshot (Autonomous Execution Spec A-04). */
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).nullable(),
  capacity: z.int().min(1).max(100_000).nullable(),
  sortOrder: z.int().min(0).max(10_000),
})
export type ConfigurationCategory = z.infer<typeof configurationCategorySchema>

// ---------------------------------------------------------------------------
// Rounds
// ---------------------------------------------------------------------------

export const configurationRoundScheduleSchema = z.strictObject({
  registrationStartAt: z.iso.datetime().nullable(),
  registrationEndAt: z.iso.datetime().nullable(),
  submissionStartAt: z.iso.datetime().nullable(),
  submissionEndAt: z.iso.datetime().nullable(),
  judgingStartAt: z.iso.datetime().nullable(),
  judgingEndAt: z.iso.datetime().nullable(),
  resultPublishAt: z.iso.datetime().nullable(),
})
export type ConfigurationRoundSchedule = z.infer<typeof configurationRoundScheduleSchema>

export const configurationManualFieldSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(80),
    minScore: z.number().finite(),
    maxScore: z.number().finite(),
    scoringEffect: manualScoringEffectSchema,
  })
  .refine((field) => field.maxScore >= field.minScore, {
    message: 'maxScore must not be below minScore.',
    path: ['maxScore'],
  })

export const configurationRoundSchema = z.strictObject({
  /** Logical key: round `key` (`preliminary` | `final`), unique per competition by construction. */
  key: roundKeySchema,
  roundOrder: z.int().min(1),
  name: z.string().trim().min(1).max(120),
  schedule: configurationRoundScheduleSchema,
  /** True if this round used the competition's single `submission_form` slot. */
  usesSubmissionForm: z.boolean(),
  /** Logical key referencing `judging.rubrics[].name`, or null. */
  defaultRubricLogicalKey: z.string().trim().min(1).max(200).nullable(),
  /** Optional so files exported before manual scoring existed still import (as `judge_only`, no fields). */
  scoringInputMode: scoringInputModeSchema.default('judge_only'),
  manualFields: z.array(configurationManualFieldSchema).max(30).default([]),
})
export type ConfigurationRound = z.infer<typeof configurationRoundSchema>

// ---------------------------------------------------------------------------
// Form field / form section (reused by registration_form, team_form,
// submission_form, judge_conflict_form)
// ---------------------------------------------------------------------------

export const configurationFormFieldSchema = z.strictObject({
  key: fieldKeySchema,
  label: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable(),
  type: formFieldTypeSchema,
  required: z.boolean(),
  placeholder: z.string().max(200).nullable(),
  defaultValue: z.string().max(2000).nullable(),
  validation: fieldValidationRulesSchema,
  options: fieldOptionsSchema,
  visibility: fieldVisibilitySchema,
  sectionLabel: z.string().trim().min(1).max(120).nullable(),
  sortOrder: z.int().min(0).max(10_000),
})
export type ConfigurationFormField = z.infer<typeof configurationFormFieldSchema>

export const configurationFormSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable(),
  fields: z.array(configurationFormFieldSchema).max(500),
})
export type ConfigurationForm = z.infer<typeof configurationFormSchema>

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export const configurationRegistrationSchema = z.strictObject({
  teamMinMembers: z.int().min(1).max(100),
  teamMaxMembers: z.int().min(1).max(100),
  allowTeacher: z.boolean(),
  requireTeacher: z.boolean(),
  allowMultipleSubmissions: z.boolean(),
  anonymousJudging: z.boolean(),
  registrationInstructions: z.string().max(20_000).nullable(),
  registrationForm: configurationFormSchema.nullable(),
  teamForm: configurationFormSchema.nullable(),
})
export type ConfigurationRegistration = z.infer<typeof configurationRegistrationSchema>

// ---------------------------------------------------------------------------
// Eligibility
// ---------------------------------------------------------------------------

export const configurationEligibilitySchema = z.strictObject({
  studentEligibilityRequired: z.boolean(),
  requirementScope: studentEligibilityRequirementScopeSchema.nullable(),
  minimumVerifiedMembers: z.int().min(0).nullable(),
  acceptedProofTypes: z.array(enrollmentProofTypeSchema),
  reviewRequired: z.boolean(),
  allowSubmitWhilePending: z.boolean(),
  proofValidityDays: z.int().min(1).nullable(),
})
export type ConfigurationEligibility = z.infer<typeof configurationEligibilitySchema>

// ---------------------------------------------------------------------------
// Submission
// ---------------------------------------------------------------------------

export const configurationSubmissionSchema = z.strictObject({
  submissionForm: configurationFormSchema.nullable(),
})
export type ConfigurationSubmission = z.infer<typeof configurationSubmissionSchema>

// ---------------------------------------------------------------------------
// Judging
// ---------------------------------------------------------------------------

export const configurationRubricItemSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable(),
  scoreType: scoreTypeSchema,
  minScore: z.number().finite().nullable(),
  maxScore: z.number().finite().nullable(),
  weight: z.number().finite().min(0).max(100),
  sortOrder: z.int().min(0).max(10_000),
  required: z.boolean(),
  options: z.array(z.string().trim().min(1).max(200)).max(100).nullable(),
  /**
   * Optional scoring bands. Absent (not null) when a criterion has none, so a
   * configuration without anchors is byte-identical to one exported before they
   * existed and still imports; the schema version stays 1.0.0 because the field
   * is additive and optional.
   */
  anchors: z.array(rubricAnchorSchema).max(12).nullable().optional(),
})
export type ConfigurationRubricItem = z.infer<typeof configurationRubricItemSchema>

export const configurationRubricSchema = z.strictObject({
  /** Logical key: rubric `name`. Must be unique within the snapshot (Autonomous Execution Spec A-04). */
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable(),
  items: z.array(configurationRubricItemSchema).min(1).max(100),
})
export type ConfigurationRubric = z.infer<typeof configurationRubricSchema>

export const configurationJudgingSchema = z.strictObject({
  rubrics: z.array(configurationRubricSchema).max(100),
  judgeConflictForm: configurationFormSchema.nullable(),
})
export type ConfigurationJudging = z.infer<typeof configurationJudgingSchema>

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export const configurationResultsSchema = z.strictObject({
  resultVisibility: resultVisibilitySchema,
})
export type ConfigurationResults = z.infer<typeof configurationResultsSchema>

// ---------------------------------------------------------------------------
// Information
// ---------------------------------------------------------------------------

export const configurationInformationSchema = z.strictObject({
  eligibilityText: z.string().max(20_000).nullable(),
  submissionRequirementsText: z.string().max(20_000).nullable(),
  judgingCriteriaText: z.string().max(20_000).nullable(),
  prizesText: z.string().max(20_000).nullable(),
  organizerContactText: z.string().max(20_000).nullable(),
  faq: z.array(publicFaqEntrySchema),
})
export type ConfigurationInformation = z.infer<typeof configurationInformationSchema>

// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------

export const configurationFeaturesSchema = z.strictObject({
  enableTeacher: z.boolean(),
  enableVideoUrl: z.boolean(),
  enablePublicGallery: z.boolean(),
  enableMultiRound: z.boolean(),
  enableTeamInvites: z.boolean(),
  reviewCommentRequired: z.boolean(),
})
export type ConfigurationFeatures = z.infer<typeof configurationFeaturesSchema>

// ---------------------------------------------------------------------------
// Root snapshot (A-01 / A-02)
// ---------------------------------------------------------------------------

export const competitionConfigurationSchema = z.strictObject({
  format: z.literal(CONFIGURATION_FORMAT),
  schemaVersion: z.string().regex(/^\d+\.\d+\.\d+$/, 'Must be a semver string.'),
  exportedAt: z.iso.datetime().optional(),
  source: z
    .strictObject({
      competitionName: z.string().max(200).optional(),
      platform: z.literal('NAPAICP').optional(),
    })
    .optional(),
  configuration: z.strictObject({
    basic: configurationBasicSchema,
    categories: z.array(configurationCategorySchema).max(500),
    rounds: z.array(configurationRoundSchema).max(50),
    registration: configurationRegistrationSchema,
    eligibility: configurationEligibilitySchema,
    submission: configurationSubmissionSchema,
    judging: configurationJudgingSchema,
    results: configurationResultsSchema,
    information: configurationInformationSchema,
    features: configurationFeaturesSchema,
  }),
})
export type CompetitionConfigurationSnapshot = z.infer<typeof competitionConfigurationSchema>

/**
 * A-06: parses only `format` + `schemaVersion` without validating the rest,
 * so an unsupported major version can be rejected as CONFIG_UNSUPPORTED_VERSION
 * before the (potentially adapter-specific) full body shape is even checked.
 */
export const configurationEnvelopeSchema = z.object({
  format: z.string(),
  schemaVersion: z.string().regex(/^\d+\.\d+\.\d+$/, 'Must be a semver string.'),
})
export type ConfigurationEnvelope = z.infer<typeof configurationEnvelopeSchema>

export function parseSchemaMajor(schemaVersion: string): number | null {
  const match = /^(\d+)\.\d+\.\d+$/.exec(schemaVersion)
  const majorGroup = match?.[1]
  if (!majorGroup) return null
  return Number.parseInt(majorGroup, 10)
}

export function isSupportedConfigurationMajor(schemaVersion: string): boolean {
  const major = parseSchemaMajor(schemaVersion)
  return major !== null && major === CONFIGURATION_SUPPORTED_MAJOR
}

// ---------------------------------------------------------------------------
// Request/response shapes: Clone (Wave C), Template (Wave D), Import (Wave F)
// ---------------------------------------------------------------------------

const newCompetitionIdentityShape = {
  name: z.string().trim().min(1).max(200),
  slug: slugSchema,
  year: z.int().min(2000).max(2999),
}

function requireAnchorDateWhenShift(
  value: { dateStrategy: ConfigurationDateStrategy; anchorDate?: string },
  ctx: z.RefinementCtx,
): void {
  if (value.dateStrategy === 'shift' && value.anchorDate === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['anchorDate'],
      message: 'anchorDate is required when dateStrategy is "shift".',
    })
  }
}

/** C-02: Clone Preflight input. Server default for `dateStrategy` is applied at the request-schema level (A-05 / AC-339), not inside the date transformer. */
export const cloneCompetitionRequestSchema = z
  .strictObject({
    ...newCompetitionIdentityShape,
    dateStrategy: configurationDateStrategySchema.default('clear'),
    anchorDate: z.iso.datetime().optional(),
    includePublicPageText: z.boolean().default(true),
    includeJudgingSettings: z.boolean().default(true),
  })
  .superRefine(requireAnchorDateWhenShift)
export type CloneCompetitionRequest = z.infer<typeof cloneCompetitionRequestSchema>

/**
 * v4.46 governance revision (`docs/sdd/08-platform-governance-workflows.md`
 * §8.1, revised 2026-08-31): Clone / Create from Template / Import Create
 * no longer provision a Competition synchronously - each submits a
 * Competition Creation Request (the same Platform Review -> Approval gate
 * every other new Competition goes through) and returns that request's id,
 * not a `competitionId`.
 */
export const configurationProvisionRequestResponseSchema = z.strictObject({
  requestId: publicIdSchema,
  status: z.string(),
  warnings: z.array(z.string()),
})
export type ConfigurationProvisionRequestResponse = z.infer<
  typeof configurationProvisionRequestResponseSchema
>

export const TEMPLATE_VISIBILITY_VALUES = ['private', 'platform'] as const
export const templateVisibilitySchema = z.enum(TEMPLATE_VISIBILITY_VALUES)
export type TemplateVisibility = z.infer<typeof templateVisibilitySchema>

/** D-04: Save as Template input. */
export const saveAsTemplateRequestSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable().default(null),
  visibility: templateVisibilitySchema,
})
export type SaveAsTemplateRequest = z.infer<typeof saveAsTemplateRequestSchema>

export const competitionTemplateDtoSchema = z.strictObject({
  id: publicIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  visibility: templateVisibilitySchema,
  schemaVersion: z.string(),
  createdByUserId: publicIdSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  currentRevisionCreatedAt: z.iso.datetime(),
})
export type CompetitionTemplateDto = z.infer<typeof competitionTemplateDtoSchema>

/** D-06: Create from Template input. */
export const createFromTemplateRequestSchema = z
  .strictObject({
    ...newCompetitionIdentityShape,
    dateStrategy: configurationDateStrategySchema.default('clear'),
    anchorDate: z.iso.datetime().optional(),
  })
  .superRefine(requireAnchorDateWhenShift)
export type CreateFromTemplateRequest = z.infer<typeof createFromTemplateRequestSchema>

/** F-05: Import Preflight result model. */
export const importPreflightIssueSchema = z.strictObject({
  code: z.string(),
  path: z.string(),
  message: z.string(),
})
export type ImportPreflightIssue = z.infer<typeof importPreflightIssueSchema>

export const importPreflightResultSchema = z.strictObject({
  ready: z.boolean(),
  sourceName: z.string().optional(),
  proposedCompetitionName: z.string(),
  schemaVersion: z.string(),
  importedSections: z.array(z.string()),
  excludedFields: z.array(z.string()),
  warnings: z.array(importPreflightIssueSchema),
  blockingErrors: z.array(importPreflightIssueSchema),
  dateStrategy: configurationDateStrategySchema,
})
export type ImportPreflightResult = z.infer<typeof importPreflightResultSchema>

/** F-03: Import Validate input. Never creates a Competition (F-03 / Wave F Gate). */
export const importValidateRequestSchema = z.strictObject({
  fileContents: z.string().max(5_000_000),
  dateStrategy: configurationDateStrategySchema.default('clear'),
  anchorDate: z.iso.datetime().optional(),
})
export type ImportValidateRequest = z.infer<typeof importValidateRequestSchema>

/** F-08: Import Create input. Server re-validates the full snapshot; never trusts a prior client-side Preflight result (F-08). */
export const importCreateRequestSchema = z
  .strictObject({
    ...newCompetitionIdentityShape,
    fileContents: z.string().max(5_000_000),
    dateStrategy: configurationDateStrategySchema.default('clear'),
    anchorDate: z.iso.datetime().optional(),
  })
  .superRefine(requireAnchorDateWhenShift)
export type ImportCreateRequest = z.infer<typeof importCreateRequestSchema>
